import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiCalendar, 
  FiHash,   
  FiExternalLink,
  FiSearch,
  FiFilter,
  FiPhoneCall,
  FiVideo
} from "react-icons/fi";
import { initializeSocket, getSocket } from "../../../utils/socket";
import axiosInstance from "../../../api/axiosInstance";

const BookingCard = ({ bookingNumber, service, date, vendorName, vendorEmail, bookingId, extraCharges = [], status, type }) => {
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const pendingCharges = (extraCharges || []).filter(c => c.status === "Pending");
  const pendingAmount = pendingCharges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  const isAudio = type?.includes("Voice") || type?.includes("Audio");
  const isCompleted = status?.toLowerCase() === "completed";
  const canJoin = status === "Accepted" || isCompleted;

  const handleJoinCall = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCompleted) {
      alert("This consultation has already been completed.");
      return;
    }
    setJoining(true);
    try {
      const res = await axiosInstance.get(`/users/bookings/${bookingId}/video-token`);
      if (res.data?.token) {
        navigate(`/video/${bookingId}?token=${res.data.token}`);
      } else {
        alert("Unable to generate call token. Please try again.");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Expert has not joined the call yet. Please try again in a moment.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="group relative bg-white border border-gray-200 rounded-2xl p-0 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden flex flex-col">
      {pendingCharges.length > 0 && (
        <div className="bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider py-1 px-3 text-center flex items-center justify-center gap-1 shadow-xs">
          <span>⚠️</span> ₹{pendingAmount.toLocaleString("en-IN")} Extra Charges Pending
        </div>
      )}
      {/* Top Content Area */}
      <div className="p-6 flex flex-col items-center text-center flex-grow">
        
        <div className="flex items-center gap-1 text-blue-600 font-bold tracking-tight mb-4">
          <FiHash size={14} />
          <span>{bookingNumber}</span>
        </div>
        
        <h3 className="text-base font-semibold text-gray-700 leading-tight mb-2">
          {service}
        </h3>
        
        <div className="flex items-center gap-2 text-gray-400 text-xs">
          <FiCalendar className="text-blue-400" />
          <span>Booking Date: {date}</span>
        </div>
      </div>

      {/* Vendor Footer (Matching the "View" layout) */}
      <div className="bg-gray-50/50 border-t border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold text-blue-800 truncate" title={`Expert: ${vendorName}`}>Expert: {vendorName}</p>
          <p className="text-[10px] text-gray-400 truncate" title={vendorEmail}>{vendorEmail}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canJoin && (
            <button
              type="button"
              onClick={handleJoinCall}
              disabled={joining || isCompleted}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${
                isCompleted
                  ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none hover:bg-gray-100 opacity-60"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm active:scale-95 disabled:opacity-50"
              }`}
              title={isCompleted ? "Booking is completed. Call is closed." : "Join Call with Expert"}
            >
              {joining ? (
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : isAudio ? (
                <FiPhoneCall size={12} />
              ) : (
                <FiVideo size={12} />
              )}
              <span>{joining ? "Joining..." : isCompleted ? "Completed" : "Join Call"}</span>
            </button>
          )}

          <Link
            to={`/profile/my-bookings/${bookingId}`}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-all shadow-md active:scale-95 whitespace-nowrap"
          >
            View <FiExternalLink size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const STATUS_FILTERS = ["All", "Pending", "Accepted", "Completed", "Rejected"];

const BookingHome = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]); // Search logic state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const bookingsPerPage = 6;

  useEffect(() => {
    fetchBookings();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const socket = initializeSocket(token) || getSocket();
    if (socket) {
      const handleStatusUpdate = (data) => {
        if (data?.bookingId || data?.bookingCode) {
          setBookings((prev) =>
            prev.map((b) =>
              b._id === data.bookingId || b.bookingId === data.bookingCode
                ? { ...b, status: data.status }
                : b
            )
          );
        }
      };
      socket.on("booking:status_updated", handleStatusUpdate);
      return () => {
        socket.off("booking:status_updated", handleStatusUpdate);
      };
    }
  }, []);

  // Update filtered list when search term or status filter changes
  useEffect(() => {
    const search = (searchTerm || "").toLowerCase();
    const results = bookings.filter(b => {
      const matchTitle = (b.serviceTitle || b.service?.title || "").toLowerCase().includes(search);
      const matchId = (b.bookingId || b._id || "").toLowerCase().includes(search);
      const matchName = `${b.vendorId?.firstName || ""} ${b.vendorId?.lastName || ""}`.toLowerCase().includes(search);
      const matchesSearch = matchTitle || matchId || matchName;
      const matchesStatus = statusFilter === "All" || b.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    setFilteredBookings(results);
    setCurrentPage(1); // Reset pagination on search/filter change
  }, [searchTerm, statusFilter, bookings]);

  const fetchBookings = async () => {
    try {
      const res = await axiosInstance.get("/users/pickslot/my");
      const data = res.data.bookings || [];
      setBookings(data);
      setFilteredBookings(data);
    } catch (err) {
      console.error("Error loading bookings");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);
  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * bookingsPerPage,
    currentPage * bookingsPerPage
  );

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-blue-600 gap-4">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="font-bold text-sm uppercase tracking-widest">Loading...</p>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header Section (Matching Account Settings) */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-sm">
            <FiCalendar className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">My Bookings</h2>
            <p className="text-xs sm:text-sm text-gray-500">Manage and track your service history</p>
          </div>
        </div>
      </div>

      {/* Search & Filter Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="relative w-full max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search bookings..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-sm"
          />
        </div>
        <div className="relative self-end md:self-auto">
          <button
            type="button"
            onClick={() => setShowFilterMenu(prev => !prev)}
            className={`flex items-center gap-2 px-5 py-2.5 bg-white border rounded-xl hover:bg-gray-50 transition-colors shadow-sm active:scale-95 ${statusFilter !== "All" ? "border-blue-300 text-blue-600" : "border-gray-200 text-gray-600"}`}
          >
            <FiFilter />
            <span className="text-sm font-bold">
              {statusFilter === "All" ? "Filters" : statusFilter}
            </span>
          </button>

          {showFilterMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowFilterMenu(false)} />
              <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-xl z-20 overflow-hidden">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setStatusFilter(s); setShowFilterMenu(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors ${
                      statusFilter === s ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="rounded-[1.5rem] border border-gray-100 bg-white shadow-xl shadow-gray-200/40 p-10 sm:p-16 text-center">
          <div className="bg-gray-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 shadow-sm">
             <FiSearch className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No results found</h3>
          <p className="text-gray-500 text-sm mt-1">Try adjusting your search criteria.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {paginatedBookings.map((b) => (
              <BookingCard
                key={b._id}
                bookingNumber={b.bookingId || b._id.slice(-8)}
                service={b.serviceTitle || "Consultation"}
                date={new Date(b.bookingDate || b.createdAt).toLocaleDateString("en-GB")}
                vendorName={`${b.vendorId?.firstName || ""} ${b.vendorId?.lastName || ""}`.trim() || "Expert"}
                vendorEmail={b.vendorId?.email || "No contact info"}
                bookingId={b._id}
                extraCharges={b.extraCharges || []}
                status={b.status}
                type={b.type}
              />
            ))}
          </div>

          {/* Professional Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-10">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl bg-white border border-gray-200 text-gray-500 hover:bg-blue-50 disabled:opacity-30 transition-all"
              >
                <FiChevronLeft size={18} />
              </button>
              
              <div className="flex items-center gap-1.5">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`min-w-[40px] h-10 rounded-xl font-bold text-xs transition-all ${
                      currentPage === i + 1 
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-100" 
                      : "bg-white border border-gray-100 text-gray-500 hover:border-blue-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl bg-white border border-gray-200 text-gray-500 hover:bg-blue-50 disabled:opacity-30 transition-all"
              >
                <FiChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingHome;